"use client";

import React from 'react';
import { Share2 } from 'lucide-react';
import { toast } from 'sonner';

interface ShareButtonProps {
    className?: string;
    url?: string;
    title?: string;
    text?: string;
}

export function ShareButton({ className = "", url, title, text }: ShareButtonProps) {
    const handleShare = async () => {
        if (typeof window === 'undefined') return;

        const shareUrl = url || window.location.href;
        const shareData: ShareData = {
            title: title || document?.title || 'Professional Profile',
            text: text || 'Check out this professional profile on Arthaverse',
            url: shareUrl,
        };

        if (typeof navigator !== 'undefined' && navigator.share) {
            try {
                if (!navigator.canShare || navigator.canShare(shareData)) {
                    await navigator.share(shareData);
                    return;
                }
            } catch (error: any) {
                // AbortError indicates user dismissed the native share sheet
                if (error?.name === 'AbortError') {
                    return;
                }
            }
        }

        // Fallback: Copy link to clipboard
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(shareUrl);
                toast.success("Profile link copied to clipboard!");
            } else {
                // Fallback for environments where clipboard API might be restricted
                const input = document.createElement('input');
                input.value = shareUrl;
                document.body.appendChild(input);
                input.select();
                document.execCommand('copy');
                document.body.removeChild(input);
                toast.success("Profile link copied to clipboard!");
            }
        } catch {
            toast.error("Failed to copy link to clipboard");
        }
    };

    return (
        <button
            type="button"
            onClick={handleShare}
            aria-label="Share profile"
            className={`border border-blue-200 text-blue-700 bg-blue-50/50 hover:bg-blue-100/60 text-xs font-semibold px-5 py-2.5 rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${className}`}
        >
            <Share2 className="w-3.5 h-3.5" />
            <span>Share Profile</span>
        </button>
    );
}

export default ShareButton;
